import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Clock, Zap, Package, Edit2, Save, X, DollarSign, AlertCircle, CheckCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

import { safeFetch } from '../../utils/safeFetch';

const AdminPricing = () => {
  const [serviceLevels, setServiceLevels] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editValues, setEditValues] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    fetchServiceLevels();
  }, []);

  const fetchServiceLevels = async () => {
    try {
      const result = await safeFetch(`${API_URL}/api/service-levels/`);
      if (result.ok && Array.isArray(result.data)) {
        const wasteLevels = result.data.filter(level => 
          level.name === 'Standard' || 
          level.name === 'Priority' || 
          level.name === 'Subscription'
        ).filter((level, index, self) => 
          index === self.findIndex(l => l.name === level.name)
        );
        setServiceLevels(wasteLevels);
      }
    } catch (err) {
      console.error('Failed to fetch service levels:', err);
      setError('Failed to load pricing');
    } finally {
      setIsLoading(false);
    }
  };

  const startEditing = (level) => {
    setEditingId(level.id);
    setEditValues({
      name: level.name,
      description: level.description,
      base_price: level.base_price,
      weekly_price: level.weekly_price,
      sla_label: level.sla_label
    });
    setError(null);
  };

  const cancelEditing = () => {
    setEditingId(null);
    setEditValues({});
    setError(null);
  };

  const saveChanges = async (levelId) => {
    setIsSaving(true);
    setError(null);
    
    try {
      const token = localStorage.getItem('admin_token');
      const result = await safeFetch(`${API_URL}/api/service-levels/${levelId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(editValues)
      });

      if (result.ok) {
        await fetchServiceLevels();
        setEditingId(null);
        setEditValues({});
        setSuccessMessage('Pricing updated successfully!');
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        if (result.status === 401) {
          setError('Session expired. Please log in again.');
        } else {
          setError(`Failed to update pricing (Error ${result.status})`);
        }
      }
    } catch (err) {
      console.error('Pricing update error:', err);
      setError('Network error - please try again');
    } finally {
      setIsSaving(false);
    }
  };

  const getIcon = (name) => {
    switch (name) {
      case 'Standard':
        return <Clock className="w-8 h-8 text-[#0D9488]" />;
      case 'Subscription':
        return <Package className="w-8 h-8 text-[#0D9488]" />;
      case 'Priority':
        return <Zap className="w-8 h-8 text-[#F59E0B]" />;
      default:
        return <DollarSign className="w-8 h-8 text-[#0D9488]" />;
    }
  };

  const formatPrice = (level) => {
    if (level.billing_type === 'WEEKLY_SUBSCRIPTION') {
      return `$${level.weekly_price?.toFixed(2) || '0.00'}`;
    }
    return `$${level.base_price?.toFixed(2) || '0.00'}`;
  };

  const getPriceLabel = (level) => {
    if (level.billing_type === 'WEEKLY_SUBSCRIPTION') {
      return '/week';
    }
    return 'AUD';
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0D9488]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-[#111111]">Pricing Management</h1>
        <p className="text-[#64748B]">Set and manage service pricing</p>
      </div>

      {/* Success Message */}
      {successMessage && (
        <div className="flex items-center gap-2 p-4 bg-green-50 border border-green-200 rounded-lg text-green-700">
          <CheckCircle className="w-5 h-5" />
          {successMessage}
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="flex items-center gap-2 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
          <AlertCircle className="w-5 h-5" />
          {error}
        </div>
      )}

      {/* Pricing Cards */}
      <div className="grid md:grid-cols-3 gap-6">
        {serviceLevels.map((level) => (
          <Card key={level.id} className={`border-[#E2E8F0] ${editingId === level.id ? 'ring-2 ring-[#0D9488]' : ''}`}>
            <CardContent className="p-6">
              {editingId === level.id ? (
                // Edit Mode
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    {getIcon(level.name)}
                    <div className="flex gap-2">
                      <Button 
                        size="sm" 
                        variant="ghost"
                        onClick={cancelEditing}
                        disabled={isSaving}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                      <Button 
                        size="sm"
                        onClick={() => saveChanges(level.id)}
                        disabled={isSaving}
                        className="bg-[#0D9488] hover:bg-[#0F766E] text-white"
                      >
                        <Save className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-[#64748B] mb-1">Name</label>
                    <Input
                      value={editValues.name}
                      onChange={(e) => setEditValues({...editValues, name: e.target.value})}
                      className="border-[#E2E8F0]"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-[#64748B] mb-1">Description</label>
                    <Input
                      value={editValues.description}
                      onChange={(e) => setEditValues({...editValues, description: e.target.value})}
                      className="border-[#E2E8F0]"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-[#64748B] mb-1">
                      {level.billing_type === 'WEEKLY_SUBSCRIPTION' ? 'Weekly Price ($)' : 'Price ($)'}
                    </label>
                    <Input
                      type="number"
                      step="0.01"
                      value={level.billing_type === 'WEEKLY_SUBSCRIPTION' ? editValues.weekly_price : editValues.base_price}
                      onChange={(e) => {
                        const value = parseFloat(e.target.value);
                        if (level.billing_type === 'WEEKLY_SUBSCRIPTION') {
                          setEditValues({...editValues, weekly_price: value});
                        } else {
                          setEditValues({...editValues, base_price: value});
                        }
                      }}
                      className="border-[#E2E8F0]"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-[#64748B] mb-1">SLA Label</label>
                    <Input
                      value={editValues.sla_label}
                      onChange={(e) => setEditValues({...editValues, sla_label: e.target.value})}
                      className="border-[#E2E8F0]"
                    />
                  </div>
                </div>
              ) : (
                // View Mode
                <>
                  <div className="flex items-start justify-between mb-4">
                    <div className="p-3 bg-[#F0FDFA] rounded-xl">
                      {getIcon(level.name)}
                    </div>
                    <Button 
                      size="sm" 
                      variant="ghost"
                      onClick={() => startEditing(level)}
                      className="text-[#64748B] hover:text-[#0D9488]"
                    >
                      <Edit2 className="w-4 h-4" />
                    </Button>
                  </div>
                  
                  <h3 className="text-lg font-semibold text-[#111111]">
                    {level.name === 'Standard' ? 'Standard Collection' : 
                     level.name === 'Priority' ? 'Priority Collection' : 
                     level.name}
                  </h3>
                  <p className="text-sm text-[#64748B] mt-1 mb-4">
                    {level.description}
                  </p>
                  
                  <div className="flex items-baseline gap-1">
                    <span className="text-3xl font-bold text-[#111111]">
                      {formatPrice(level)}
                    </span>
                    <span className="text-sm text-[#64748B]">{getPriceLabel(level)}</span>
                  </div>
                  
                  <div className="flex items-center gap-2 mt-4">
                    <div className="w-2 h-2 rounded-full bg-green-500"></div>
                    <span className="text-sm text-green-600">Active</span>
                  </div>
                  
                  <p className="text-xs text-[#94A3B8] mt-4">
                    SLA: {level.sla_label}
                  </p>
                </>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Info Note */}
      <Card className="border-[#E2E8F0] bg-[#F0FDFA]">
        <CardContent className="p-4">
          <div className="flex gap-3">
            <DollarSign className="w-5 h-5 text-[#0D9488] mt-0.5" />
            <div>
              <h4 className="font-medium text-[#0F766E]">Pricing Note</h4>
              <p className="text-sm text-[#134E4A]">
                Price changes will apply to new bookings only. Existing bookings will retain their original pricing. 
                All prices are in Australian Dollars (AUD) and include GST.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminPricing;
